import { add } from 'date-fns';
import { create } from 'zustand';

import {
  TASK_ASSIGNER_FILTERS_NAME,
  TaskAssignerFilters,
  TaskAssignerSortBy,
} from '../../types';

export type SortOrder = 'asc' | 'desc';

export interface Sorter {
  sortBy: TaskAssignerSortBy;
  order: SortOrder;
}

/** Default execution date window: last 7 days through next 7 days (aligned with task_assigns backend defaults). */
export function getTaskAssignerDefaultDateRange(): { from: Date; to: Date } {
  const today = new Date();
  return {
    from: add(today, { days: -7 }),
    to: add(today, { days: 7 }),
  };
}

function getDefaultFilters(): TaskAssignerFilters {
  return {
    [TASK_ASSIGNER_FILTERS_NAME.DATE_RANGE]: getTaskAssignerDefaultDateRange(),
    [TASK_ASSIGNER_FILTERS_NAME.LOCATION]: null,
    [TASK_ASSIGNER_FILTERS_NAME.LOCATION_TYPE]: null,
    [TASK_ASSIGNER_FILTERS_NAME.FORM]: null,
    [TASK_ASSIGNER_FILTERS_NAME.STATUS]: null,
    [TASK_ASSIGNER_FILTERS_NAME.USER]: null,
    [TASK_ASSIGNER_FILTERS_NAME.GROUP]: null,
    [TASK_ASSIGNER_FILTERS_NAME.TASK_ID]: null,
    [TASK_ASSIGNER_FILTERS_NAME.ASSET]: null,
    [TASK_ASSIGNER_FILTERS_NAME.ORIGIN]: null,
  };
}

interface TaskAssignerFilterStore {
  searchQuery: string;
  currentPage: number;
  filters: TaskAssignerFilters;
  sorter: Sorter;

  actions: {
    setSearchQuery: (query: string) => void;
    setCurrentPage: (page: number) => void;
    setFilter: <K extends TASK_ASSIGNER_FILTERS_NAME>(
      key: K,
      value: TaskAssignerFilters[K],
    ) => void;
    resetFilter: (key: TASK_ASSIGNER_FILTERS_NAME) => void;
    resetFilters: () => void;
    setSorter: (sorter: Sorter) => void;
  };
}

export const useTaskAssignerFilterStore = create<TaskAssignerFilterStore>((set) => {
  const defaultFilters = getDefaultFilters();
  return {
    searchQuery: '',
    currentPage: 0,
    filters: { ...defaultFilters },
    sorter: { sortBy: 'id', order: 'asc' },

    actions: {
      setSearchQuery: (query) => set({ searchQuery: query, currentPage: 0 }),

      setCurrentPage: (page) => set({ currentPage: page }),

      setFilter: (key, value) =>
        set((state) => {
          if (JSON.stringify(state.filters[key]) === JSON.stringify(value)) return state;
          return {
            filters: { ...state.filters, [key]: value },
            currentPage: 0,
          };
        }),

      resetFilter: (key) =>
        set((state) => ({
          filters: { ...state.filters, [key]: getDefaultFilters()[key] },
          currentPage: 0,
        })),

      resetFilters: () =>
        set({
          filters: getDefaultFilters(),
          currentPage: 0,
        }),

      setSorter: (sorter) => set({ sorter }),
    },
  };
});
